/**
 * Composable for managing Auth0 authentication and user-related operations.
 * Provides methods for login, email verification, and social login.
 *
 * @returns {Object} An object containing authentication-related methods and states
 * @property {Function} emailExists - Checks if an email exists in the system
 * @property {Function} emailExistsCheckout - Checks email existence during checkout
 * @property {Function} login - Performs username/password login
 * @property {Ref<string | null>} loginError - Stores login error messages
 * @property {Function} checkPsw - Validates user password
 * @property {Function} socialLogin - Initiates social login
 * @property {Function} getSFUserFromEmail - Retrieves Salesforce user by email
 */
export const useAuth0 = () => {
  const { dispatchAccountLoginEvent } = useGAAccountEvents()
  const isLoading = useLoaderState()

  const config = useRuntimeConfig()

  /**
   * State for storing login error messages.
   * @type {Ref<string | null | undefined>}
   */
  const loginError = useState<string | null | undefined>(
    'auth0-login-error',
    () => null
  )

  const { registerSuccessful } = useRouteHelper()

  // Determine current domain for redirect URI
  let currentDomain = ''
  try {
    const url = window ? window.location.href : ''
    const urlObject = new URL(url)
    currentDomain = urlObject.protocol + '//' + urlObject.host || ''
  } catch {
    currentDomain = ''
  }

  /**
   * Determines the redirect URI for authentication.
   * @type {string}
   */
  const redirectUri =
    currentDomain !== ''
      ? `${currentDomain}/api/auth0/authorize`
      : config.public.auth0.redirectUri

  // @see https://github.com/auth0/auth0.js
  /**
   * Auth0 configuration options.
   * @type {Object}
   */
  const opts = {
    domain: config.public.auth0.domain,
    clientID: config.public.auth0.clientID,
    redirectUri: redirectUri,
    scope:
      'openid profile email read:current_user update:current_user_metadata create:current_user_metadata delete:current_user_metadata',
    audience: config.public.auth0.audience,
  }

  /**
   * Checks if an email exists in the system.
   *
   * @param {string} email - Email to check
   * @param {boolean} [showLoader=true] - Whether to show loading state
   * @returns {Promise<any>} Result of email existence check
   * @throws {Error} If the check fails
   */
  const emailExists = async (email: string, showLoader = true) => {
    if (showLoader) isLoading.value = true
    try {
      const data = await $fetch('/api/auth0/checkEmailExists', {
        method: 'POST',
        body: { email: email.toLowerCase() },
      })
      return data
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      if (showLoader) isLoading.value = false
    }
  }

  /**
   * Checks if an email exists during checkout process.
   *
   * @param {string} email - Email to check
   * @param {boolean} [showLoader=true] - Whether to show loading state
   * @returns {Promise<any>} Result of email existence check
   * @throws {Error} If the check fails
   */
  const emailExistsCheckout = async (email: string, showLoader = true) => {
    if (showLoader) isLoading.value = true
    try {
      const data = await $fetch('/api/auth0/checkEmailExistsCheckout', {
        method: 'POST',
        body: { email: email.toLowerCase() },
      })
      return data
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      if (showLoader) isLoading.value = false
    }
  }

  /**
   * Retrieves Salesforce user by email.
   *
   * @param {string} email - User email
   * @returns {Promise<any>} Salesforce user data
   * @throws {Error} If user retrieval fails
   */
  const getSFUserFromEmail = async (email: string) => {
    try {
      const data = await $fetch('/api/auth0/getSfUser', {
        method: 'POST',
        body: { email: email.toLowerCase() },
      })
      return data
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  /**
   * Performs user login with Auth0.
   *
   * @param {string} username - User email
   * @param {string} [password] - User password
   * @param {'registration-successful' | 'current-page' | 'no-update'} [redirectType] - Updates the cookie that handles the redirection after login/registration:
   *    - redirect to current page before login/registration (default behaviour, when logging in from the sidebar)
   *    - redirect to specific registration successful page (when registering from the /signup page)
   *    - no cookie update and redirect to previously saved value (when logging in from the /signup page)
   * @throws {Error} If login fails
   *
   * @example
   * await login('user@example.com', 'password', false);
   */
  const login = async (
    username: string,
    password?: string,
    redirectType:
      | 'registration-successful'
      | 'current-page'
      | 'no-update' = 'current-page'
  ) => {
    const auth0 = await import('auth0-js')
    const webAuth = new auth0.WebAuth(opts)

    // could be called without password
    if (!password) return
    // set current page cookie
    const currentPageBeforeLogin = useCookie('page-before-login', {
      domain: import.meta.dev ? 'localhost' : '.armani.com',
    })

    switch (redirectType) {
      case 'registration-successful':
        currentPageBeforeLogin.value = registerSuccessful
        break
      case 'no-update':
        break
      case 'current-page':
      default:
        currentPageBeforeLogin.value = window ? window.location.href : ''
    }

    // EIFE-3659: registration from the signup page opened by a private sale campaign
    // (see fetchPage.global.ts). The Auth0 callback checks the campaign allow-list
    // and, when eligible, redirects to `callback` instead of registration-successful.
    const privateSaleRegistration = useCookie<{
      content: string
      callback: string
    } | null>('private-sale-registration', {
      domain: import.meta.dev ? 'localhost' : '.armani.com',
      maxAge: 60 * 10,
    })
    const signupQuery = new URLSearchParams(window.location.search)
    const privateSaleContent = signupQuery.get('content')
    const privateSaleCallback = signupQuery.get('callback')
    privateSaleRegistration.value =
      redirectType === 'registration-successful' &&
      privateSaleContent &&
      privateSaleCallback
        ? { content: privateSaleContent, callback: privateSaleCallback }
        : null

    if (redirectType === 'registration-successful') {
      const successfulRegistered = useCookie('successfulRegistered')
      successfulRegistered.value = 'successfulRegistered'
    } else {
      // Store a flag for registration event
      localStorage.setItem('pendingLoginEvent', 'true')
    }

    loginError.value = null

    // @see https://github.com/auth0/auth0.js

    webAuth.login(
      {
        email: username.toLowerCase(),
        password: password,
        realm: 'Username-Password-Authentication',
        scope:
          'offline_access openid profile email read:current_user update:current_user_metadata create:current_user_metadata delete:current_user_metadata',
        audience: `https://${config.public.auth0.audienceDomain}/api/v2/`,
        responseType: 'code',
      },
      e => {
        console.log(e)
        loginError.value = e?.error_description || e?.error
      }
    )
  }

  /**
   * Checks user password validity.
   *
   * @param {string} username - User email
   * @param {string} [password] - User password
   * @returns {Promise<any>} Token or validation result
   */
  const checkPsw = async (username: string, password?: string) => {
    if (!password) return

    const data = await $fetch(`/api/auth0/getTokenPswGrant`, {
      method: 'POST',
      body: { username: username.toLowerCase(), password },
    })
    return data
  }

  /**
   * Initiates social login for a specific connection.
   *
   * @param {string} connection - Social login connection type (e.g., 'google', 'facebook')
   * @throws {Error} If social login authorization fails
   *
   * @example
   * await socialLogin('google');
   */
  const socialLogin = async (connection: string) => {
    const currentPageBeforeLogin = useCookie('page-before-login', {
      domain: import.meta.dev ? 'localhost' : '.armani.com',
    })

    // This code avoids redirecting the user to the signup page in case they log in from there
    // Instead, the previous value of the cookie is used (i.e. the page before signup)
    if (!window.location.href.includes('/signup/')) {
      currentPageBeforeLogin.value = window ? window.location.href : ''
    }

    const redirectUri =
      currentDomain !== ''
        ? `${currentDomain}/api/auth0/authorizeSocialLogin`
        : config.public.auth0.redirectSocialUri

    const auth0 = await import('auth0-js')

    const webAuth = new auth0.WebAuth({
      domain: opts.domain,
      clientID: opts.clientID,
      redirectUri: redirectUri,
    })

    webAuth.authorize({
      connection: connection,
      responseType: 'code',
      scope:
        'openid profile email read:current_user update:current_user_metadata create:current_user_metadata delete:current_user_metadata',
      audience: `https://${config.public.auth0.audienceDomain}/api/v2/`,
    })
  }

  // Dispatch login event on component mount if a pending login event exists
  onMounted(() => {
    const pendingLoginEvent = localStorage.getItem('pendingLoginEvent')

    if (pendingLoginEvent === 'true') {
      dispatchAccountLoginEvent()
      localStorage.removeItem('pendingLoginEvent')
    }
  })

  return {
    emailExists,
    emailExistsCheckout,
    login,
    loginError,
    checkPsw,
    socialLogin,
    getSFUserFromEmail,
  }
}
